"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Logo from "@/app/(components)/generics/Logo/Logo";
import { UStyles } from "@/app/utils/UStyles";
import styles from "./PageLoader.module.css";

type PageLoaderProps = {
  active?: boolean;
  delay?: number;
  minDuration?: number;
};

export default function PageLoader(props: PageLoaderProps) {
  const { active = true, delay = 300, minDuration = 300 } = props;

  const [visible, setVisible] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const shownAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (active) {
      setFadingOut(false);
      if (visible) {
        return;
      }
      // Wait `delay` before showing: a load that resolves quickly never
      // flashes the loader at all.
      const timer = setTimeout(() => {
        setVisible(true);
        shownAtRef.current = Date.now();
      }, delay);
      return () => clearTimeout(timer);
    }

    if (!visible) {
      return;
    }

    // Once shown, stay at least `minDuration` so a fast resolve doesn't blink
    // the loader off the screen, then fade out.
    const elapsed = shownAtRef.current ? Date.now() - shownAtRef.current : 0;
    const remaining = Math.max(0, minDuration - elapsed);
    const timer = setTimeout(() => setFadingOut(true), remaining);
    return () => clearTimeout(timer);
  }, [active, visible, delay, minDuration]);

  useEffect(() => {
    if (!visible || typeof document === "undefined") {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [visible]);

  const handleAnimationEnd = () => {
    if (fadingOut) {
      setVisible(false);
      setFadingOut(false);
      shownAtRef.current = null;
    }
  };

  if (!visible || typeof document === "undefined") {
    return null;
  }

  // Portal to <body> so the fixed overlay always covers the whole viewport,
  // even when rendered from inside a transformed ancestor (e.g. the sidebar),
  // where `position: fixed` would otherwise be confined to that ancestor.
  return createPortal(
    <div
      className={UStyles.classNames(styles.container, fadingOut ? styles.fadingOut : "")}
      onAnimationEnd={handleAnimationEnd}
    >
      <Logo max blinker />
    </div>,
    document.body,
  );
}
